import React, { useEffect, useMemo, useState } from "react";
import { Modal, Button } from "react-bootstrap";
import { DndContext, closestCenter } from "@dnd-kit/core";
import { formatPrice } from 'utils'
import { v4 as uuidv4 } from "uuid";
import Swal from "sweetalert2";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  arrayMove
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useSelector } from "react-redux";
import api from "api";

// ─────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────

function emptyLine() {
  return { id: uuidv4(), label: "", amount: 0 };
}

function initLines(items) {
  if (!items?.length) return [];
  return items.map(d => ({
    id: d.id || uuidv4(),
    code: d.code || "",
    label: d.label || "",
    amount: d.amount || 0
  }));
}

function validateLines(lines, entityName) {
  for (const line of lines) {
    if (!line.label?.trim()) {
      Swal.fire({ text: `Tous les ${entityName} doivent avoir un libellé.`, showConfirmButton: true });
      return false;
    }
    if (!line.amount || isNaN(line.amount) || Number(line.amount) <= 0) {
      Swal.fire({ text: `Tous les ${entityName} doivent avoir un montant valide.`, showConfirmButton: true });
      return false;
    }
  }
  return true;
}

// ─────────────────────────────────────────────
// SortableRow (générique)
// ─────────────────────────────────────────────

function SortableRow({ line, onChange, onRemove, amountPlaceholder }) {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: line.id });

  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className="d-flex gap-2 align-items-center mb-2"
    >
      <span {...attributes} {...listeners} style={{ cursor: "grab" }}>☰</span>

      <input
        className="form-control form-control-sm"
        placeholder="Libellé"
        value={line.label}
        onChange={e => onChange({ ...line, label: e.target.value })}
      />

      <input
        type="number"
        className="form-control form-control-sm"
        placeholder={amountPlaceholder}
        value={line.amount}
        onChange={e => onChange({ ...line, amount: parseFloat(e.target.value) || 0 })}
      />

      <Button variant="outline-danger" size="sm" onClick={onRemove}>×</Button>
    </div>
  );
}

// ─────────────────────────────────────────────
// SortableList (générique)
// ─────────────────────────────────────────────

function SortableList({ lines, setLines, addLabel, amountPlaceholder }) {
  function onDragEnd({ active, over }) {
    if (!over || active.id === over.id) return;
    setLines(items => {
      const oldIndex = items.findIndex(i => i.id === active.id);
      const newIndex = items.findIndex(i => i.id === over.id);
      return arrayMove(items, oldIndex, newIndex);
    });
  }

  return (
    <>
      <DndContext collisionDetection={closestCenter} onDragEnd={onDragEnd}>
        <SortableContext items={lines.map(l => l.id)} strategy={verticalListSortingStrategy}>
          {lines.map(line => (
            <SortableRow
              key={line.id}
              line={line}
              amountPlaceholder={amountPlaceholder}
              onChange={val => setLines(list => list.map(i => (i.id === line.id ? val : i)))}
              onRemove={() => setLines(list => list.filter(i => i.id !== line.id))}
            />
          ))}
        </SortableContext>
      </DndContext>
      <Button
        variant="outline-secondary"
        size="sm"
        onClick={() => setLines(l => [...l, emptyLine()])}
      >
        {addLabel}
      </Button>
    </>
  );
}

// ─────────────────────────────────────────────
// Modal principale
// ─────────────────────────────────────────────

export default function AdminFormalitiesPricingModal({
  show,
  onClose,
  onSaved,
  pricing = null
}) {
  const vat_rate = useSelector(state => state.config.vat_rate);

  const [fpId] = useState(pricing?.fpId ?? null);
  const [label, setLabel] = useState(pricing?.fpLabel ?? "");
  const [honorairesET, setHonorairesET] = useState(pricing?.fpHonorairesET ?? 0);
  const [debours, setDebours] = useState([]);
  const [frais, setFrais] = useState([]);

  useEffect(() => {
    setDebours(initLines(pricing?.fpDisbursements));
    setFrais(initLines(pricing?.fpFees));
  }, [pricing]);

  // ── Calculs en centimes ──────────────────────

  const toCents = val => Math.round((Number(val) || 0) * 100);
  const sumCents = lines => lines.reduce((s, l) => s + toCents(l.amount), 0);

  const honorairesETCents = useMemo(() => toCents(honorairesET), [honorairesET]);
  const honorairesVATCents = useMemo(() => Math.round(honorairesETCents * Number(vat_rate) / 100), [honorairesETCents, vat_rate]);
  const honorairesITCents = useMemo(() => honorairesETCents + honorairesVATCents, [honorairesETCents, honorairesVATCents]);

  const deboursTotalCents = useMemo(() => sumCents(debours), [debours]);

  const fraisETCents = useMemo(() => sumCents(frais), [frais]);
  const fraisVATCents = useMemo(() => Math.round(fraisETCents * Number(vat_rate) / 100), [fraisETCents, vat_rate]);
  const fraisITCents = useMemo(() => fraisETCents + fraisVATCents, [fraisETCents, fraisVATCents]);

  const totalITCents = useMemo(
    () => honorairesITCents + deboursTotalCents + fraisITCents,
    [honorairesITCents, deboursTotalCents, fraisITCents]
  );

  // ── Conversion euros ─────────────────────────
  const honorairesVAT = honorairesVATCents / 100;
  const honorairesIT = honorairesITCents / 100;
  const deboursTotalIT = deboursTotalCents / 100;
  const fraisET = fraisETCents / 100;
  const fraisVAT = fraisVATCents / 100;
  const fraisIT = fraisITCents / 100;
  const totalIT = totalITCents / 100;

  // ── Sauvegarde ───────────────────────────────

  function handleSave() {
    if (!validateLines(debours, "débours")) return;
    if (!validateLines(frais, "frais")) return;

    const stripId = ({ id, ...rest }) => rest;

    const payload = {
      fpLabel: label,
      fpHonorairesET: Number(honorairesET),
      fpHonorairesVATRate: Number(vat_rate),
      fpHonorairesVATAmount: honorairesVAT,
      fpHonorairesIT: honorairesIT,
      fpDisbursements: debours.map(stripId),
      fpDisbursementsIT: deboursTotalIT,   // ← ici
      fpFees: frais.map(stripId),
      fpFeesET: fraisET,
      fpFeesVATAmount: fraisVAT,
      fpFeesIT: fraisIT,
      fpTotalPriceIT: totalIT
    };

    const req = fpId
      ? api.put(`/admin/formalities/pricings/${fpId}`, payload)
      : api.post(`/admin/formalities/pricings`, payload);

    req.then(() => onSaved());
  }

  // ── Rendu ────────────────────────────────────

  return (
    <Modal show={show} onHide={onClose} size="lg" backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>{fpId ? "Modifier le tarif" : "Nouveau tarif"}</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {/* Libellé + Honoraires */}
        <div className="row mb-3">
          <div className="col-sm-8">
            <label className="form-label fs-7 fw-bold">Libellé</label>
            <input
              className="form-control"
              value={label}
              onChange={e => setLabel(e.target.value)}
            />
          </div>
          <div className="col-sm-4">
            <label className="form-label fs-7 fw-bold">Honoraires HT</label>
            <input
              type="number"
              className="form-control"
              value={honorairesET}
              onChange={e => setHonorairesET(parseFloat(e.target.value) || 0)}
            />
          </div>
        </div>

        {/* Frais */}
        <h5 className="fs-7 fw-bold mt-3">Frais (HT)</h5>
        <SortableList
          lines={frais}
          setLines={setFrais}
          addLabel="+ Ajouter un frais"
          amountPlaceholder="Montant HT"
        />

        {/* Débours */}
        <h5 className="fs-7 fw-bold mt-4">Débours (TTC)</h5>
        <SortableList
          lines={debours}
          setLines={setDebours}
          addLabel="+ Ajouter un débours"
          amountPlaceholder="Montant TTC"
        />

        {/* Récapitulatif */}
        <hr />
        <div className="text-end">
          <div>Frais HT : {formatPrice(fraisET)}</div>
          <div>Frais TTC ({vat_rate}%) : {formatPrice(fraisIT)}</div>
          <div>Débours TTC : {formatPrice(deboursTotalIT)}</div>
          <div>Honoraires TTC ({vat_rate}%) : {formatPrice(honorairesIT)}</div>
          <strong>Total TTC : {formatPrice(totalIT)}</strong>
        </div>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>Annuler</Button>
        <Button variant="primary" onClick={handleSave}>Enregistrer</Button>
      </Modal.Footer>
    </Modal>
  );
}
